import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import PropTypes from 'prop-types';

import {Box, Button, Divider, Paper, Stack, Typography} from '@mui/material';

import {useGetAccountAlertsQuery, useLazyGetAccountAlertsQuery} from '../../../../features/coreApi/apiSlice';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../../../app/store/reducers/session';
import {AlertsColumns, valueGetter} from '../../../../commons/constants/tableColumns';
import {StyledTable} from '../../../../commons/components/@extended/CustomTables';
import {CustomNoRowsOverlay, ExportToolBarWithSearch} from '../../../../commons/components/@extended/DataGridTables';
import {sortBooleans, sortNumbers, sortStrings} from '../../../../commons/utils/sorting';
import TableBodyCell from '../../../../commons/components/@extended/TableBodyCell';
import LoadingBox, {ComplexLoadingBox} from '../../../../commons/components/@extended/LoadingBox';
import EmptyBox from '../../../../commons/components/@extended/EmptyBox';
import dayjs from 'dayjs';
import {ButtonDateRangePicker} from '../../../../commons/components/@extended/CustomButtons';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';

const shortcutsItems = [
    {
        label: 'Last 7 days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        }
    },
    {
        label: 'Last 30 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(30, 'day'), today];
        }
    },
    {
        label: 'Last 60 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(60, 'day'), today];
        }
    }
];

const AlertsTable = ({alerts}) => {
    const columns = useMemo(
        () => [
            ...AlertsColumns.map((column) => ({
                ...column,
                field: column.id,
                headerName: column.label,
                renderCell: (params) => <TableBodyCell column={column} params={params} />,
                renderEditCell: (params) => <TableBodyCell column={column} params={params} />,
                valueFormatter: (params) => valueGetter(params, column),
                sortComparator: column.numeric
                    ? (v1, v2) => sortNumbers(v1, v2)
                    : column.boolean
                      ? (v1, v2) => sortBooleans(v1, v2)
                      : (v1, v2) => sortStrings(v1, v2)
            }))
        ],
        []
    );

    return (
        <div style={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column'}}>
            <StyledTable
                columns={columns}
                rows={[...alerts]}
                pagination
                pageSizeOptions={[10, 20, 50, 100]}
                slots={{
                    noRowsOverlay: () => <CustomNoRowsOverlay msg={'There are no recent alerts'} />,
                    toolbar: ExportToolBarWithSearch
                }}
                initialState={{
                    pagination: {paginationModel: {pageSize: 10}}
                }}
                autoHeight
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: '0.75rem'
                    },
                    '& .MuiDataGrid-root': {
                        height: '100%'
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        flex: 1,
                        overflow: 'auto'
                    },
                    '& .MuiDataGrid-footerContainer': {
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider',
                        minHeight: '50px !important',
                        height: '50px !important',
                        alignItems: 'center'
                    },
                    '& .MuiDataGrid-cell': {
                        whiteSpace: 'normal !important',
                        lineHeight: '1.3 !important',
                        padding: '8px !important',
                        paddingLeft: '16px !important',
                        overflow: 'auto',
                        justifyContent: 'flex-start !important'
                    },
                    '& .MuiDataGrid-cellContent': {
                        justifyContent: 'flex-start !important',
                        textAlign: 'left !important'
                    },
                    '& .MuiDataGrid-row': {
                        maxHeight: 'none !important',
                        height: 'auto !important'
                    },
                    '& .MuiBox-root': {
                        alignItems: 'flex-start'
                    }
                }}
            />
        </div>
    );
};

AlertsTable.propTypes = {
    alerts: PropTypes.array.isRequired
};

const DataAlerts = () => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);

    const [rangeDate, setRangeDate] = useState([null, null]);
    const [selectedValue, setSelectedValue] = useState('Last 30 Days');

    const {
        data: alerts,
        isLoading,
        isFetching,
        isSuccess,
        isError
    } = useGetAccountAlertsQuery({sfid: params?.account_sfid}, {skip: !params?.account_sfid});
    const [
        fetchAlerts,
        {
            data: alertsCustomDateData,
            isLoading: isLoadingAlerts,
            isFetching: isFetchingAlerts,
            isSuccess: isSuccessAlerts,
            isError: isErrorAlerts
        }
    ] = useLazyGetAccountAlertsQuery();

    const displayData = useMemo(() => {
        // If we have custom date filtered data, use that
        if (isSuccessAlerts && alertsCustomDateData) {
            return alertsCustomDateData;
        }

        if (isSuccess && alerts) {
            return alerts;
        }

        return [];
    }, [isSuccessAlerts, alertsCustomDateData, alerts, isSuccess]);

    const alertsData = useMemo(() => {
        if (displayData?.length > 0) {
            const filteredAlerts =
                isStakeAdmin || isAdministrator
                    ? displayData
                    : displayData.filter((alert) => propertyPermissions.includes(alert.property_sfid));

            if (params?.campaign_sfid) {
                return filteredAlerts.filter((alert) => alert.campaign_sfid === params.campaign_sfid);
            } else if (params?.community_sfid) {
                return filteredAlerts.filter((alert) => alert.property_sfid === params.community_sfid);
            }

            return filteredAlerts.map((alert) => ({
                ...alert
            }));
        }

        return [];
    }, [displayData, params]);

    const handleDateRangeChange = (newValue) => {
        setRangeDate(newValue);
        setSelectedValue('custom');

        if (newValue[0] && newValue[1]) {
            const startDate = newValue[0].format('YYYY-MM-DD');
            const endDate = newValue[1].format('YYYY-MM-DD');
            fetchAlerts({sfid: params?.account_sfid, startDate, endDate});
        }
    };

    const handleSelectChange = (value) => {
        setRangeDate([null, null]);
        setSelectedValue(value);

        if (value !== 'custom') {
            const {getValue} = shortcutsItems.find((item) => item.label === value);
            const [startDate, endDate] = getValue();
            const startDateFormatted = startDate.format('YYYY-MM-DD');
            const endDateFormatted = endDate.format('YYYY-MM-DD');
            fetchAlerts({sfid: params?.account_sfid, startDate: startDateFormatted, endDate: endDateFormatted});
        }
    };

    const alertTableComponent = useMemo(() => {
        const isComponentLoading = isLoading || isFetching || isFetchingAlerts || isLoadingAlerts;
        if (isComponentLoading) {
            return <ComplexLoadingBox isLoading={isComponentLoading} />;
        }

        if (isError || isErrorAlerts) {
            return (
                <Box sx={{height: '200px', width: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Typography variant="subtitle1" fontWeight={'fontWeightRegular'} color={'text.subtitle'}>
                        Failed to fetch alerts
                    </Typography>
                </Box>
            );
        }

        if (alertsData?.length > 0) {
            return <AlertsTable alerts={alertsData} />;
        } else if (alertsData?.length === 0) {
            return <EmptyBox text={'There are no recent alerts'} height="20vh" />;
        }
    }, [isLoading, isFetching, isError, isSuccess, alertsData]);

    return (
        <Paper sx={{borderRadius: 2, boxShadow: 'none'}}>
            <Stack direction={'column'} divider={<Divider />}>
                <Stack direction="column" rowGap={2} sx={{p: 2}}>
                    <Stack direction={'column'}>
                        <Typography variant="h5" fontWeight="fontWeightMedium">
                            Alerts
                        </Typography>
                        <Typography variant="body2" color={'text.subtitle'}>
                            Monitor and track errors with your resident data that Stake receives from your PMS to ensure that your Stake
                            programs are running smoothly. These alerts help you stay informed about the health of your resident data
                            integration and ensure data quality across your system.
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        {shortcutsItems.map((item) => (
                            <Button
                                key={item.label}
                                size="small"
                                variant={selectedValue == item.label ? 'contained' : 'outlined'}
                                color="secondary"
                                onClick={() => handleSelectChange(item.label)}
                            >
                                {item.label}
                            </Button>
                        ))}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <ButtonDateRangePicker
                                label={
                                    rangeDate[0] === null && rangeDate[1] === null
                                        ? null
                                        : rangeDate.map((date) => (date ? date.format('MM/DD/YYYY') : 'null')).join(' - ')
                                }
                                value={rangeDate}
                                onChange={(newValue) => handleDateRangeChange(newValue)}
                            />
                        </LocalizationProvider>
                    </Stack>
                </Stack>
                {alertTableComponent}
            </Stack>
        </Paper>
    );
};

DataAlerts.propTypes = {
    alerts: PropTypes.array.isRequired
};

export default DataAlerts;
